import { Vector3 } from 'three';
import type { Pcg32 } from '../../../sim/rng';
import type { PixelCamera } from '../../camera/PixelCamera';
import { axisQuat, type Batch, put, QUARTER_TURN, TAU, yawQuat } from './batch';
import { MAX_PARTICLES } from './batches';
import type { BurstSpec } from './bursts';

/*
 * The particle pool (05 §9): debris cubes on ballistic arcs, and puffs of smoke, dust and
 * snow. Other effects borrow the same pool for their own short-lived kinds and register how
 * those are drawn, so there is one cap and one loop. Nothing fades: things shrink in steps.
 */

/** Snappier than real gravity, so arcs read at game speed. */
const GRAVITY = 14;
/** Sizes animate on a stepped clock, like hand-drawn frames; positions stay smooth. */
const STEP_HZ = 15;
/** Anything smaller than this (metres) is under a texel: drop it rather than draw noise. */
const MIN_SIZE = 0.05;
const TUMBLE_AXIS = new Vector3(0.64, 0.42, 0.64).normalize();

export type ParticleKind = 'cube' | 'puff' | 'fireball' | 'flash' | 'slash' | 'marker';

/** Pooled and reused: every field is overwritten by `spawn`. */
export interface Particle {
  kind: ParticleKind;
  batch: Batch;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Metres: cube edge, puff or fireball radius, or a plain scale for flashes. */
  size: number;
  /** Seconds; negative while a delayed particle waits its turn. */
  age: number;
  life: number;
  /** Ground height under a cube, re-sampled only when it comes down. */
  floor: number;
  /** 0 flying, 1 bounced once, 2 at rest. */
  bounces: number;
  yaw: number;
  /** Tumble rate, rad/s, shown in quarter-turn steps. */
  spin: number;
  /** Id of the sim entity this particle belongs to (bark markers), else -1. */
  ref: number;
}

/** Writes a particle's instance for this frame. */
export type Drawer = (p: Particle, camera: PixelCamera) => void;

const drawNothing: Drawer = () => {};

const drawCube: Drawer = (p, camera) => {
  // Shrinks away in three steps over the last quarter of its life.
  const t = p.age / p.life;
  const s = t > 0.75 ? (p.size * Math.ceil(((1 - t) / 0.25) * 3)) / 3 : p.size;
  const angle = p.yaw + Math.round((p.age * p.spin) / QUARTER_TURN) * QUARTER_TURN;
  put(p.batch, camera, p.x, p.y, p.z, s, s, s, axisQuat(TUMBLE_AXIS, angle));
};

const drawPuff: Drawer = (p, camera) => {
  const tq = Math.min(1, Math.floor(p.age * STEP_HZ) / STEP_HZ / p.life);
  const k = tq < 0.2 ? 0.5 + tq * 2.5 : tq < 0.45 ? 1 : (1 - tq) / 0.55;
  const s = p.size * k;
  if (s >= MIN_SIZE) put(p.batch, camera, p.x, p.y, p.z, s, s, s, yawQuat(p.yaw));
};

export class ParticleSystem {
  private readonly pool: Particle[] = [];
  private live = 0;
  private readonly drawers: Record<ParticleKind, Drawer> = {
    cube: drawCube,
    puff: drawPuff,
    fireball: drawNothing,
    flash: drawNothing,
    slash: drawNothing,
    marker: drawNothing,
  };

  /** `idle` is only what unused pool entries point at; `rng` is the effects layer's shared stream. */
  constructor(
    private readonly rng: Pcg32,
    private readonly groundY: (x: number, z: number) => number,
    idle: Batch,
  ) {
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.pool.push({ kind: 'cube', batch: idle, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, size: 0, age: 0, life: 1, floor: 0, bounces: 0, yaw: 0, spin: 0, ref: -1 });
    }
  }

  /** The effect that owns a particle kind supplies how it is drawn. */
  setDrawer(kind: ParticleKind, drawer: Drawer): void {
    this.drawers[kind] = drawer;
  }

  /**
   * Claim a pooled particle. When the pool is full the one nearest its end is recycled,
   * debris and smoke before anything else: a fireball cut short is what the eye would miss.
   */
  spawn(kind: ParticleKind, batch: Batch, x: number, y: number, z: number, size: number, life: number): Particle {
    let p: Particle;
    if (this.live < MAX_PARTICLES) {
      p = this.at(this.live);
      this.live++;
    } else {
      p = this.at(0);
      let most = -Infinity;
      for (let i = 0; i < this.live; i++) {
        const q = this.at(i);
        const done = q.age / q.life + (q.kind === 'cube' || q.kind === 'puff' ? 1 : 0);
        if (done > most) {
          most = done;
          p = q;
        }
      }
    }
    p.kind = kind;
    p.batch = batch;
    p.x = x;
    p.y = y;
    p.z = z;
    p.vx = 0;
    p.vy = 0;
    p.vz = 0;
    p.size = size;
    p.age = 0;
    p.life = life;
    p.floor = y;
    p.bounces = 0;
    p.yaw = 0;
    p.spin = 0;
    p.ref = -1;
    return p;
  }

  cube(batch: Batch, x: number, y: number, z: number, size: number, life: number, vx: number, vy: number, vz: number, floor: number): void {
    const p = this.spawn('cube', batch, x, y, z, size, life);
    p.vx = vx;
    p.vy = vy;
    p.vz = vz;
    p.floor = floor;
    p.yaw = this.rng.range(0, TAU);
    p.spin = this.rng.range(6, 16);
  }

  /** A blob that swells, drifts up at `rise` m/s against drag, and shrinks away. */
  puff(batch: Batch, x: number, y: number, z: number, size: number, life: number, rise: number, delay: number): Particle {
    const p = this.spawn('puff', batch, x, y, z, size, life);
    p.vy = rise;
    p.age = -delay;
    p.yaw = this.rng.range(0, TAU);
    return p;
  }

  /** Cubes thrown outward and up from a point; `reach` scales the horizontal speed. */
  burst(spec: BurstSpec, tones: readonly Batch[], count: number, x: number, y: number, z: number, floor: number, reach: number): void {
    const rng = this.rng;
    for (let i = 0; i < count; i++) {
      const a = rng.range(0, TAU);
      const speed = rng.range(spec.speed[0], spec.speed[1]) * reach;
      this.cube(rng.pick(tones), x, y, z, rng.range(spec.size[0], spec.size[1]), rng.range(spec.life[0], spec.life[1]), Math.sin(a) * speed, rng.range(spec.up[0], spec.up[1]), Math.cos(a) * speed, floor);
    }
  }

  /** End, on the next step, every live particle of `kind` tagged with `ref`. */
  retire(kind: ParticleKind, ref: number): void {
    for (let i = 0; i < this.live; i++) {
      const p = this.at(i);
      if (p.kind === kind && p.ref === ref) p.age = p.life;
    }
  }

  /** Draw, move and age every live particle; the finished are swapped out of the live range. */
  step(dt: number, camera: PixelCamera): void {
    let i = 0;
    while (i < this.live) {
      const p = this.at(i);
      if (p.age >= p.life) {
        // Swap-remove: live particles stay packed at the front of the pool.
        const last = this.live - 1;
        this.pool[i] = this.at(last);
        this.pool[last] = p;
        this.live = last;
        continue;
      }
      // Draw first, then move: a fresh particle is always seen where it was born.
      if (p.age >= 0) {
        this.drawers[p.kind](p, camera);
        this.move(p, dt);
      }
      p.age += dt;
      i++;
    }
  }

  clear(): void {
    this.live = 0;
  }

  private at(index: number): Particle {
    const p = this.pool[index];
    if (!p) throw new Error(`no particle ${index}`);
    return p;
  }

  private move(p: Particle, dt: number): void {
    if (p.kind === 'cube') {
      if (p.bounces >= 2) return;
      p.vy -= GRAVITY * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      const half = p.size / 2;
      if (p.vy < 0 && p.y - half <= p.floor) {
        // Only now look the ground up where the cube really is; in flight the launch height will do.
        p.floor = this.groundY(p.x, p.z);
        if (p.y - half > p.floor) return;
        p.y = p.floor + half;
        p.bounces++;
        if (p.bounces === 1) {
          p.vy *= -0.4;
          p.vx *= 0.5;
          p.vz *= 0.5;
        } else {
          p.spin = 0;
        }
      }
    } else if (p.kind === 'puff') {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      const drag = Math.max(0, 1 - 1.1 * dt);
      p.vx *= drag;
      p.vy *= drag;
      p.vz *= drag;
    }
  }
}
